import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { sellerHome } from "@/lib/onboarding";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const ROLES = ["buyer", "seller", "developer"];
const MODES = ["login", "register", "forgot", "reset", "verify"];

const AUTH_IMG = "https://images.pexels.com/photos/8108560/pexels-photo-8108560.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000";
const field = "w-full px-4 py-3 bg-aura-surface border border-aura-border focus:border-aura-accent outline-none text-sm";

export default function Auth() {
  const { login, register, startSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const modeFromParams = () => (MODES.includes(params.get("mode")) ? params.get("mode") : "register");
  const [mode, setMode] = useState(modeFromParams);
  const roleFromParams = () => {
    const r = params.get("role");
    if (ROLES.includes(r)) return r;
    return params.get("mode") === "register" ? "buyer" : "seller";
  };
  const [role, setRole] = useState(roleFromParams);

  useEffect(() => {
    setMode(modeFromParams());
    setRole(roleFromParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const [form, setForm] = useState({ name: "", email: "", password: "", next: "", again: "" });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const token = params.get("token") || "";

  const enter = (user) => {
    toast.success(`Welcome${user.name ? `, ${user.name.split(" ")[0]}` : ""}`);
    navigate(sellerHome(user));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        enter(await login(form.email, form.password));
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot", { email: form.email });
        setPending({ email: form.email, detail: data.detail });
      } else if (mode === "reset") {
        if (form.next !== form.again) {
          toast.error("The new passwords do not match.");
          return;
        }
        const { data } = await api.post("/auth/reset", { token, password: form.next });
        enter(startSession(data));
      } else if (mode === "verify") {
        const { data } = await api.post("/auth/verify", { token });
        enter(startSession(data));
      } else {
        const data = await register({ name: form.name, email: form.email, password: form.password, role });
        setPending({ email: data.email || form.email, detail: "We sent a confirmation link. Open it to finish creating the account." });
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!form.email && !pending?.email) {
      toast.error("Enter the email on the account.");
      return;
    }
    setLoading(true);
    try {
      const email = pending?.email || form.email;
      const path = mode === "forgot" || pending?.detail?.includes("reset") ? "/auth/forgot" : "/auth/resend";
      const { data } = await api.post(path, { email });
      toast.success(data.detail);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const title = {
    login: "Welcome back",
    forgot: "Reset your password",
    reset: "Choose a new password",
    verify: "Confirm your email",
    register: "Join Shoptosa",
  }[mode];

  const blurb = mode === "login"
    ? "Sign in to your account."
    : mode === "forgot"
      ? "We will email a reset link if this address has an account."
      : mode === "reset"
        ? "Use at least 8 characters. This link works once."
        : mode === "verify"
          ? "This confirms the inbox can receive mail from Shoptosa."
          : role === "seller"
            ? "Create a seller account, then confirm the email before you pick a plan."
            : role === "developer"
              ? "Create a free developer account. Confirm the email, then your key is ready in the console."
              : "Create a free shopper account. We email a confirmation link before you can sign in.";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="Marketplace" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-aura-bg/40 to-transparent" />
        <div className="absolute bottom-12 left-12 text-white max-w-sm">
          <div className="font-serif text-4xl leading-tight tracking-tight">The marketplace that knows your taste.</div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <Logo size={36} />
          </div>
          <div className="font-serif text-3xl tracking-tight mb-2">{title}</div>
          <p className="text-sm text-aura-muted mb-8">{blurb}</p>

          {pending ? (
            <div className="border border-aura-border p-6" data-testid="auth-pending">
              <div className="font-medium mb-2">{pending.email}</div>
              <p className="text-sm text-aura-muted leading-relaxed">{pending.detail}</p>
              <button type="button" onClick={resend} disabled={loading} className="mt-4 text-sm link-underline" data-testid="auth-resend">
                Send the email again
              </button>
            </div>
          ) : (
            <>
              {mode === "register" && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => navigate(`/auth?mode=register&role=${r}`)}
                      data-testid={`role-${r}`}
                      className={`py-3 text-sm capitalize border transition-colors ${
                        role === r ? "border-aura-accent bg-aura-accent text-white" : "border-aura-border hover:border-aura-accent"
                      }`}
                    >
                      I'm a {r}
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <input
                    required value={form.name} onChange={set("name")} placeholder="Full name"
                    data-testid="auth-name" className={field}
                  />
                )}
                {(mode === "login" || mode === "register" || mode === "forgot") && (
                  <input
                    required type="email" value={form.email} onChange={set("email")} placeholder="Email"
                    data-testid="auth-email" className={field}
                  />
                )}
                {(mode === "login" || mode === "register") && (
                  <input
                    required type="password" minLength={mode === "register" ? 8 : undefined}
                    value={form.password} onChange={set("password")} placeholder="Password"
                    data-testid="auth-password" className={field}
                  />
                )}
                {mode === "reset" && (
                  <>
                    <input required type="password" minLength={8} value={form.next} onChange={set("next")} placeholder="New password" data-testid="auth-new-password" className={field} />
                    <input required type="password" minLength={8} value={form.again} onChange={set("again")} placeholder="Repeat new password" data-testid="auth-repeat-password" className={field} />
                  </>
                )}
                {mode === "login" && (
                  <div className="flex justify-end">
                    <button type="button" onClick={() => navigate("/auth?mode=forgot")} className="text-sm link-underline" data-testid="auth-forgot">
                      Forgot password?
                    </button>
                  </div>
                )}
                <button
                  type="submit" disabled={loading || (mode === "verify" && !token) || (mode === "reset" && !token)}
                  data-testid="auth-submit"
                  className="w-full py-3.5 btn-accent rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mode === "login" && "Sign in"}
                  {mode === "forgot" && "Email me a reset link"}
                  {mode === "reset" && "Update password"}
                  {mode === "verify" && "Confirm email"}
                  {mode === "register" && (role === "seller" ? "Create seller account" : role === "developer" ? "Create developer account" : "Create free account")}
                </button>
                {mode === "register" && (
                  <p className="text-xs text-aura-muted">
                    We check that this domain can receive mail, then send a confirmation link. The account stays closed until you open it.
                  </p>
                )}
                {mode === "register" && role === "seller" && (
                  <p className="text-xs text-aura-muted">
                    Plans start at ₹499/month. You will pay with Cashfree (UPI / cards) after the email is confirmed.
                  </p>
                )}
                {mode === "login" && (
                  <button type="button" onClick={resend} className="text-xs text-aura-muted link-underline" data-testid="auth-resend-confirm">
                    Send the confirmation email again
                  </button>
                )}
              </form>
            </>
          )}

          <div className="mt-6 text-sm text-aura-muted">
            {mode === "login" ? "New to Shoptosa?" : "Already have an account?"}{" "}
            <button
              onClick={() => { setPending(null); navigate(mode === "login" ? "/auth?mode=register" : "/auth?mode=login"); }}
              data-testid="auth-toggle"
              className="text-aura-ink font-medium link-underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
